import { useState, useEffect, useRef } from 'react';
import { fetchPracticeTests } from '../../helpers/practiceTests';
import { useAppointment } from '../../context/appointmentContext';
import { useTestContext } from '../../context/testContext';
import { useResultData } from '../../helpers/useResultData';
import { usePostMessage } from '../../context/messageContext';
import { useSessionState } from '../../context/sessionStateContext';
import { testsMap } from '../../helpers/data';
import { SessionActions, useSessionUI } from '../../ui';
import { ExamPickerView } from './exams/ExamPickerView';
import { toPickerCategories, examDisplayName } from './exams/examCatalog';

// A re-joining clinician is taken back into the test they were running, once per page load.
let restoreAttempted = false;

// C6 Examinations picker. The tests enabled for the appointment's practice come from
// api/appointments/<id>/tests/ (fetchPracticeTests); Next stores { eyeTestId, testKey } in the test context.
const SelectTest = ({ setActiveTab, setTitle }) => {
  const [selectedTest, setSelectedTest] = useState(null);
  const { updateTestData } = useTestContext();
  const [testCategories, setTestCategories] = useState([]);
  const [status, setStatus] = useState('loading');
  const { clearAllResults } = useResultData();
  const postMessage = usePostMessage();
  const { state: sessionState, status: sessionStatus, patch } = useSessionState();
  const { toast } = useSessionUI();
  const cleared = useRef(false);
  const { appointment } = useAppointment() || {};
  const appointmentId = appointment?.currentAppointment?.id;

  useEffect(() => {
    setTitle('Examinations');
  }, [setTitle]);

  useEffect(() => {
    clearAllResults();
    postMessage({ type: 'holding' });
  }, [clearAllResults, postMessage]);

  const activateTest = (test = selectedTest) => updateTestData({ eyeTestId: test.test_id, testKey: test.key });

  useEffect(() => {
    let live = true;
    const getCategorisedTests = async () => {
      try {
        const enabled = await fetchPracticeTests(appointmentId);
        if (!live) return;
        setTestCategories(toPickerCategories(enabled, (key) => Boolean(testsMap[key])));
        setStatus('ready');
      } catch (e) {
        if (live) setStatus('error');
      }
    };
    getCategorisedTests();
    return () => {
      live = false;
    };
  }, [appointmentId]);

  // Rejoin: the shared session state still names the test that was open -> reopen it (from its
  // setup step, since the eye/answers of the interrupted run only lived in the other tab).
  // Otherwise record that the clinician is on the picker.
  useEffect(() => {
    if (status !== 'ready' || sessionStatus === 'loading' || sessionStatus === 'idle') return;
    const key = sessionState?.test_key;
    if (!restoreAttempted && sessionState?.step === 'test' && key) {
      restoreAttempted = true;
      const test = testCategories.flatMap((c) => c.tests).find((t) => t.key === key);
      if (test) {
        toast({ tone: 'info', title: `Resumed ${examDisplayName(test)}`, body: 'The test starts again from its setup step.' });
        activateTest(test);
        return;
      }
    }
    restoreAttempted = true;
    if (cleared.current) return;
    cleared.current = true;
    if (key || sessionState?.sub_step) patch({ step: 'test', test_key: null, sub_step: null }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sessionStatus, testCategories]);

  return (
    <>
      <ExamPickerView categories={testCategories} selected={selectedTest} onSelect={setSelectedTest} status={status} />
      <SessionActions onBack={() => setActiveTab('set-distance')} primary={{ label: 'Next', onClick: () => activateTest(), disabled: !selectedTest }} />
    </>
  );
};

export default SelectTest;
